import { ComponentProps } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Button } from 'components/Button/Button';
import { Icon } from 'components/Icon';
import { Image } from 'components/Image/Image';
import { CopyClipboardButton, ShareButton } from 'components/SocialButton';
import { Body } from 'components/Text/Body';
import { BodySmall } from 'components/Text/BodySmall';
import { H3 } from 'components/Text/H3';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { CmsApiPersonaResult } from 'modules/cms/api/types/blocks/CmsApiPersonaQuizBlock';
import { colors } from 'theme';
import { imageUrl } from 'utils/filestack';

const socialEvent = 'Share Persona Quiz Results';

const StyledImage = styled(
  ({
    isPage,
    className,
    ...props
  }: ComponentProps<typeof Image> & {
    isPage?: boolean;
    className?: string;
    alt?: string;
  }) => (
    <Box className={className}>
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Image {...props} />
    </Box>
  ),
)`
  img {
    display: block;
    margin: 0 auto;
    max-height: 300px;
    max-width: 100%;
    @supports (object-fit: contain) {
      width: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
`;

type Props = {
  blockIndex?: number;
  result: CmsApiPersonaResult;
  content?: CmsApiPage;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickRetake?: (...args: Array<any>) => any;
};

export function CmsPersonaQuizBlockResult({
  blockIndex,
  result: { title, description, image, socialMediaText },
  content,
  clickRetake,
}: Props) {
  const message = socialMediaText;
  return (
    <Box
      borderRadius={32}
      data-qa-id="cms-persona-quiz-results"
      overflow="hidden"
      bg={colors.inputGrey}
    >
      <Box
        px={32}
        py={16}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        backgroundImage={`linear-gradient(
        to left,
        ${colors.skyBlue},
        ${colors.hoverBlue}
      )`}
        color="white"
      >
        <BodySmall>{getText('Results')}</BodySmall>

        <Box display="flex" alignItems="center">
          <Button onClick={clickRetake}>
            <Icon name="refresh" size={16} />
          </Button>
          <BodySmall>{getText('Retake')}</BodySmall>
        </Box>
      </Box>

      <Box p={32}>
        <Box
          display={['block', null, 'flex']}
          p={[12, null, 32]}
          borderRadius={16}
          bg="white"
        >
          <Box flex="0 0 auto" width={[1, null, 1 / 2]}>
            <H3 data-qa-id="result-title">{title}</H3>
            <Body
              analyticsData={
                blockIndex
                  ? {
                      eventName: 'CMS Block',
                      eventProperties: {
                        block_position: blockIndex,
                        block_type: 'Persona Quiz',
                      },
                    }
                  : {}
              }
              data-qa-id="result-description"
              dangerouslySetInnerHTML={{
                // @ts-expect-error TODO fix me
                __html: description,
              }}
            />

            {content && (
              <Box
                mt={32}
                mx="-4px"
                display="flex"
                flexWrap="wrap"
                gridGap="8px"
                my="4px"
              >
                <ShareButton
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="facebook"
                  utmParams={{}}
                  message={message}
                  eventName={socialEvent}
                  eventProperties={{
                    type: 'Facebook',
                  }}
                />

                <ShareButton
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="twitter"
                  utmParams={{}}
                  message={message}
                  eventName={socialEvent}
                  eventProperties={{
                    type: 'Twitter',
                  }}
                />

                <ShareButton
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="linkedin"
                  utmParams={{}}
                  message={message}
                  eventName={socialEvent}
                  eventProperties={{
                    type: 'LinkedIn',
                  }}
                />

                <CopyClipboardButton
                  object={content}
                  objectType="CMS_PAGE"
                  data-qa-id="persona-quiz-copy-link"
                  utmParams={{}}
                />
              </Box>
            )}
          </Box>

          {image && (
            <Box data-qa-id="result-image" flex="1 1 auto" mt={[24, null, 0]}>
              <StyledImage
                src={imageUrl({
                  data: image,
                  width: undefined,
                  height: undefined,
                  fit: 'max',
                  insecureSVG: false,
                })}
                alt={title || image.altText || ''}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
